import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { Loader2, Search, ChevronRight, Pencil, X, ShieldOff, ShieldCheck, KeyRound, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_BADGE = {
  CONNECTED: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  AWAITING_RECONNECT: 'bg-amber-50 text-amber-700 border-amber-300',
  PENDING_PROVISION: 'bg-blue-50 text-blue-700 border-blue-300',
  DISCONNECTED: 'bg-gray-100 text-gray-600 border-gray-300',
  NONE: 'bg-gray-50 text-gray-500 border-gray-200',
};

export default function AdminVendors() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [plan, setPlan] = useState('');
  const [status, setStatus] = useState('');
  const [editing, setEditing] = useState(null); // seller being edited

  const load = () => {
    setLoading(true);
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (plan) params.plan = plan;
    if (status) params.status = status;
    api.get('/admin/vendors', { params })
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-3" data-testid="admin-vendors">
      <div className="fab-card p-4 flex flex-wrap gap-2 items-end" data-testid="admin-vendors-filters">
        <div className="flex-1 min-w-[200px]">
          <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold mb-1">Search</div>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-fab-text-muted" />
            <input
              data-testid="vendors-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && load()}
              placeholder="business name, email, owner…"
              className="w-full pl-8 pr-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px] focus:outline-none focus:border-fab-primary"
            />
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold mb-1">Plan</div>
          <select data-testid="vendors-plan-filter" value={plan} onChange={(e) => setPlan(e.target.value)} className="px-2 py-2 rounded-lg border border-fab-primary-lighter text-[13px]">
            <option value="">All</option>
            <option value="FREE_TRIAL">FREE_TRIAL</option>
            <option value="STANDARD">STANDARD</option>
            <option value="PRO">PRO</option>
            <option value="ENTERPRISE">ENTERPRISE</option>
          </select>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold mb-1">Status</div>
          <select data-testid="vendors-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="px-2 py-2 rounded-lg border border-fab-primary-lighter text-[13px]">
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
        <button data-testid="vendors-apply-filters" onClick={load} className="btn-primary px-3 py-2 rounded-lg text-[13px] font-semibold">Apply</button>
      </div>

      <div className="fab-card overflow-hidden">
        {loading ? (
          <div className="flex justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-fab-primary" /></div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-fab-text-muted text-[13px]">No vendors match the current filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]" data-testid="vendors-table">
              <thead className="bg-fab-elevated text-[11px] uppercase tracking-wider text-fab-text-muted">
                <tr>
                  <th className="text-left px-3 py-2">Business</th>
                  <th className="text-left px-3 py-2">Owner / Email</th>
                  <th className="text-left px-3 py-2">WhatsApp</th>
                  <th className="text-left px-3 py-2">Plan</th>
                  <th className="text-left px-3 py-2">Signup</th>
                  <th className="text-left px-3 py-2">Last login</th>
                  <th className="text-right px-3 py-2">P / C / G / Conv</th>
                  <th className="text-right px-3 py-2">Edit / View</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-fab-primary-lighter">
                {items.map((v) => (
                  <tr key={v.id} data-testid={`vendor-row-${v.id}`} className={`hover:bg-fab-elevated/50 ${v.suspended ? 'opacity-60' : ''}`}>
                    <td className="px-3 py-2">
                      <div className="font-semibold text-fab-text">{v.business_name}</div>
                      {v.is_admin && <span className="text-[10px] font-mono font-bold text-fab-primary">ADMIN</span>}
                      {v.suspended && <span className="text-[10px] font-mono font-bold text-red-600 ml-1">SUSPENDED</span>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-fab-text">{v.owner_name}</div>
                      <div className="text-fab-text-muted text-[11.5px]">{v.email}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-mono text-[12px]">{v.whatsapp_phone || '—'}</div>
                      <span className={`inline-block text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded border mt-0.5 ${STATUS_BADGE[v.whatsapp_status] || STATUS_BADGE.NONE}`}>{v.whatsapp_status}</span>
                    </td>
                    <td className="px-3 py-2 font-mono font-semibold">{v.plan_tier}</td>
                    <td className="px-3 py-2 text-fab-text-muted text-[11.5px]">{v.created_at ? new Date(v.created_at).toLocaleDateString() : '—'}</td>
                    <td className="px-3 py-2 text-fab-text-muted text-[11.5px]">{v.last_login_at ? new Date(v.last_login_at).toLocaleDateString() : 'never'}</td>
                    <td className="px-3 py-2 text-right font-mono text-[11.5px]">{v.products_count} / {v.customers_count} / {v.groups_count} / {v.conversations_count}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        data-testid={`seller-edit-btn-${v.id}`}
                        onClick={() => setEditing(v)}
                        title="Edit seller"
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-fab-primary/40 text-fab-primary hover:bg-fab-primary/5"
                      ><Pencil className="w-3.5 h-3.5" /></button>
                      <Link
                        to={`/dashboard/admin/vendors/${v.id}/view`}
                        data-testid={`vendor-view-${v.id}`}
                        title="View seller"
                        className="ml-2 inline-flex items-center justify-center w-8 h-8 rounded-lg border border-fab-primary-lighter text-fab-text-secondary hover:bg-fab-elevated align-middle"
                      ><ChevronRight className="w-4 h-4" /></Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="px-3 py-2 text-[11px] text-fab-text-muted border-t border-fab-primary-lighter">{items.length} vendor{items.length === 1 ? '' : 's'} shown</div>
      </div>

      {editing && (
        <EditSellerModal seller={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function EditSellerModal({ seller, onClose, onSaved }) {
  const [suspended, setSuspended] = useState(!!seller.suspended);
  const [confirmDeact, setConfirmDeact] = useState(false);
  const [email, setEmail] = useState(seller.email || '');
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [busy, setBusy] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const isAdminAccount = !!(seller.is_admin || seller.is_platform_admin);

  const doActivate = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/vendors/${seller.id}/activate`, {});
      setSuspended(false);
      toast.success('Seller activated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Activation failed'); }
    finally { setBusy(false); }
  };
  const doDeactivate = async () => {
    setBusy(true);
    try {
      const reason = '';
      await api.post(`/admin/vendors/${seller.id}/suspend`, { reason });
      setSuspended(true);
      setConfirmDeact(false);
      toast.success('Seller deactivated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Deactivation failed'); }
    finally { setBusy(false); }
  };

  const save = async () => {
    // Validate password pair if attempting to set one
    if (pwd || pwd2) {
      if (pwd.length < 8) return toast.error('Password must be at least 8 characters');
      if (pwd !== pwd2) return toast.error('Passwords do not match');
    }
    setBusy(true);
    try {
      let changed = false;
      const trimmed = email.trim();
      if (trimmed && trimmed.toLowerCase() !== (seller.email || '').toLowerCase()) {
        await api.post(`/admin/vendors/${seller.id}/update-email`, { new_email: trimmed });
        changed = true;
      }
      if (pwd) {
        await api.post(`/admin/vendors/${seller.id}/reset-password`, { new_password: pwd });
        changed = true;
      }
      toast.success(changed ? 'Seller updated' : 'No login changes to save');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="edit-seller-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-fab-primary-lighter">
          <div>
            <div className="font-display text-base font-bold text-fab-text">Edit Seller</div>
            <div className="text-[12px] text-fab-text-muted" data-testid="edit-seller-name">{seller.business_name}</div>
          </div>
          <button data-testid="edit-cancel-x" onClick={onClose} className="p-1 rounded-lg hover:bg-fab-elevated"><X className="w-4 h-4 text-fab-text-muted" /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Account status */}
          <div className="border border-fab-primary/40 rounded-lg p-3">
            <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold mb-2">Account Status</div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[12px] text-fab-text-secondary">Current:</span>
              <span data-testid="edit-status-badge" className={`text-[11px] font-mono font-bold px-2 py-0.5 rounded border ${suspended ? 'bg-red-50 text-red-600 border-red-300' : 'bg-emerald-50 text-emerald-700 border-emerald-300'}`}>
                {suspended ? 'DEACTIVATED' : 'ACTIVE'}
              </span>
            </div>
            {suspended ? (
              <button data-testid="edit-activate-btn" disabled={busy} onClick={doActivate} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-semibold text-emerald-700 border border-emerald-300 hover:bg-emerald-50 disabled:opacity-50">
                <ShieldCheck className="w-4 h-4" /> Activate Seller
              </button>
            ) : confirmDeact ? (
              <div className="bg-red-50/60 border border-red-200 rounded-lg p-3">
                <div className="text-[12.5px] text-red-700 mb-2">Are you sure you want to deactivate this seller?</div>
                <div className="flex gap-2">
                  <button data-testid="edit-deactivate-cancel" disabled={busy} onClick={() => setConfirmDeact(false)} className="px-3 py-1.5 rounded-lg text-[12.5px] font-semibold border border-fab-primary-lighter text-fab-text-secondary">Cancel</button>
                  <button data-testid="edit-deactivate-confirm" disabled={busy} onClick={doDeactivate} className="px-3 py-1.5 rounded-lg text-[12.5px] font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                    {busy ? 'Deactivating…' : 'Deactivate'}
                  </button>
                </div>
              </div>
            ) : (
              <button data-testid="edit-deactivate-btn" disabled={busy} onClick={() => setConfirmDeact(true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-semibold text-red-600 border border-red-300 hover:bg-red-50 disabled:opacity-50">
                <ShieldOff className="w-4 h-4" /> Deactivate Seller
              </button>
            )}
          </div>

          {/* Login email */}
          <div className="border border-fab-primary/40 rounded-lg p-3">
            <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold mb-2">Login Email</div>
            <input
              data-testid="edit-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px] focus:outline-none focus:border-fab-primary"
              placeholder="seller@email.com"
            />
          </div>

          {/* Password */}
          <div className="border border-fab-primary/40 rounded-lg p-3">
            <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold mb-2 flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" /> Set / Reset Password</div>
            <input
              data-testid="edit-password-input"
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px] mb-2 focus:outline-none focus:border-fab-primary"
              placeholder="New password (min 8 chars)"
              autoComplete="new-password"
            />
            <input
              data-testid="edit-password-confirm-input"
              type="password"
              value={pwd2}
              onChange={(e) => setPwd2(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px] focus:outline-none focus:border-fab-primary"
              placeholder="Confirm new password"
              autoComplete="new-password"
            />
            <div className="text-[10.5px] text-fab-text-muted mt-1">Leave blank to keep the current password. The existing password is never shown.</div>
          </div>

          {/* Danger zone — permanent delete */}
          <div className="border border-red-300 rounded-lg p-3 bg-red-50/40">
            <div className="text-[11px] uppercase tracking-wider text-red-600 font-semibold mb-2 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Danger Zone</div>
            {isAdminAccount ? (
              <div className="text-[12px] text-fab-text-muted">Admin accounts cannot be deleted.</div>
            ) : (
              <>
                <div className="text-[12px] text-fab-text-secondary mb-2">Permanently delete this seller and all its account-owned trial data so the login email can be reused. This cannot be undone.</div>
                <button data-testid="edit-delete-seller-btn" disabled={busy} onClick={() => setShowDelete(true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50">
                  <Trash2 className="w-4 h-4" /> Delete Seller
                </button>
              </>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-fab-primary-lighter">
          <button data-testid="edit-cancel-btn" disabled={busy} onClick={onClose} className="px-4 py-2 rounded-lg text-[13px] font-semibold border border-fab-primary-lighter text-fab-text-secondary hover:bg-fab-elevated">Cancel</button>
          <button data-testid="edit-save-btn" disabled={busy} onClick={save} className="btn-primary px-4 py-2 rounded-lg text-[13px] font-semibold disabled:opacity-50">
            {busy ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
      {showDelete && (
        <DeleteSellerConfirm seller={seller} onClose={() => setShowDelete(false)}
          onDeleted={() => { setShowDelete(false); onSaved(); }} />
      )}
    </div>
  );
}

function DeleteSellerConfirm({ seller, onClose, onDeleted }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const target = (seller.email || '').trim();
  const canDelete = text.trim().toLowerCase() === target.toLowerCase() && target.length > 0;

  const doDelete = async () => {
    if (!canDelete) return;
    setBusy(true);
    try {
      await api.delete(`/admin/vendors/${seller.id}`, { params: { confirm_email: text.trim() } });
      toast.success('Seller deleted permanently.');
      onDeleted();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="delete-seller-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-red-200">
          <AlertTriangle className="w-5 h-5 text-red-600" />
          <div className="font-display text-base font-bold text-red-700">Delete Seller?</div>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-[13px] text-fab-text-secondary">This will <b>permanently delete</b> this seller account and its account-owned trial data (products, catalogue, customers, chats, orders, invoices and more). Audit logs are preserved. <b>This action cannot be undone.</b></div>
          <div className="bg-fab-elevated/60 rounded-lg px-3 py-2 text-[13px]">
            <div className="font-semibold text-fab-text" data-testid="delete-seller-name">{seller.business_name || seller.name || 'Seller'}</div>
            <div className="text-fab-text-muted font-mono text-[12px]" data-testid="delete-seller-email">{target}</div>
          </div>
          <div>
            <label className="text-[12px] text-fab-text-secondary">Type the seller's login email to confirm:</label>
            <input
              data-testid="delete-seller-confirm-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px] focus:outline-none focus:border-red-400"
              placeholder={target}
              autoFocus
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-fab-primary-lighter">
          <button data-testid="delete-seller-cancel" disabled={busy} onClick={onClose} className="px-4 py-2 rounded-lg text-[13px] font-semibold border border-fab-primary-lighter text-fab-text-secondary hover:bg-fab-elevated">Cancel</button>
          <button data-testid="delete-seller-confirm" disabled={!canDelete || busy} onClick={doDelete} className="px-4 py-2 rounded-lg text-[13px] font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed">
            {busy ? 'Deleting…' : 'Delete Permanently'}
          </button>
        </div>
      </div>
    </div>
  );
}
